import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarCheck, CheckCircle, ShoppingBag, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Footer from "../components/Footer";
import Logo from "../components/Logo";

const BUSINESS_TYPES = [
  "Food",
  "Retail",
  "Coffee Shop",
  "Convenience Store",
  "Dessert Shop",
  "Off Licence",
  "Vape Shop",
  "Beauty / Hair",
  "Services",
  "Other",
];

const INITIAL = {
  business_name: "",
  contact_name: "",
  business_type: "",
  email: "",
  phone: "",
  address: "",
  message: "",
  services: ["Online ordering"],
};

const SERVICE_OPTIONS = [
  {
    id: "Online ordering",
    icon: ShoppingBag,
    title: "Online ordering",
    blurb: "Your menu on My Local. Orders straight to the kitchen.",
  },
  {
    id: "Table bookings",
    icon: CalendarCheck,
    title: "Table bookings",
    blurb: "Let regulars book a table from their phone.",
  },
];

const FIELD_LABELS = {
  business_name: "Business name",
  contact_name: "Contact name",
  business_type: "Business type",
  email: "Email address",
  phone: "Phone number",
  address: "Business address",
};

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="mt-1.5 text-xs text-red-600">{msg}</p>;
}

function Label({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-zinc-900 mb-2">
      {children}
      {required && <span className="text-[#FF1F8B] ml-1">*</span>}
    </label>
  );
}

const inputClass =
  "w-full rounded-2xl bg-white border border-zinc-200 px-4 py-3.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#FF1F8B] focus:ring-4 focus:ring-[#FF1F8B]/10 transition-all";

export default function PartnerSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Become a Partner | My Local";
    return () => {
      document.title = "My Local | Food Delivery & Takeaways in Strabane & Lifford";
    };
  }, []);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((errs) => ({ ...errs, [k]: undefined }));
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id)
        ? f.services.filter((s) => s !== id)
        : [...f.services, id],
    }));
    if (errors.services) setErrors((errs) => ({ ...errs, services: undefined }));
  };

  const validate = () => {
    const e = {};
    const required = ["business_name", "contact_name", "business_type", "email", "phone", "address"];
    required.forEach((k) => {
      if (!form[k] || !String(form[k]).trim()) e[k] = `${FIELD_LABELS[k]} is required`;
    });
    if (!form.services.length) e.services = "Pick at least one";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Please enter a valid email address";
    }
    if (form.phone && form.phone.replace(/\D/g, "").length < 5) {
      e.phone = "Please enter a valid phone number";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/partners/signup", form);
      setSubmitted(true);
      toast.success("Submitted!", { description: "We'll be in touch soon." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Submission failed", {
        description: typeof detail === "string" ? detail : "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partner-signup-page" className="relative bg-[#FAFAFA] min-h-screen">
      {/* Top bar */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-8 md:pt-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to My Local
        </Link>
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size="md" withText={false} />
        </Link>
      </div>

      {/* Hero + form */}
      <section className="relative overflow-hidden pt-12 md:pt-16 pb-24">
        <div className="aura -top-40 -right-40 opacity-60" />
        <div className="aura -bottom-40 -left-40 opacity-40" />

        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="overline text-zinc-500 mb-5 flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full brand-gradient" />
                Become a partner
              </p>
              <h1 className="font-display font-extrabold text-zinc-900 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                Join <span className="gradient-text">My Local.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
                Online ordering, table bookings, or both. Fill in your details
                below and we'll get back to you.
              </p>

              <div className="mt-10 space-y-4">
                {[
                  { label: "Low fixed percentage", body: "Helping local businesses keep more of their profits, right here in the area." },
                  { label: "Free sign-up", body: "Start accepting orders on our Android POS or your phone." },
                  { label: "Real local support", body: "Run by people who live and shop in Strabane, just like you." },
                ].map((it) => (
                  <div key={it.label} className="flex items-start gap-3">
                    <div className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full brand-gradient text-white">
                      <CheckCircle size={14} weight="fill" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">{it.label}</p>
                      <p className="text-sm text-zinc-500">{it.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right: form / success */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-[28px] bg-white border border-zinc-100 shadow-[0_30px_80px_-30px_rgba(15,15,20,0.25)] p-6 md:p-10">
              <div className="absolute -top-px left-10 right-10 h-px brand-gradient" />

              {submitted ? (
                <div data-testid="signup-success" className="py-6 text-center">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full brand-gradient text-white mb-6">
                    <CheckCircle size={32} weight="fill" />
                  </div>
                  <h2 className="font-display font-extrabold text-zinc-900 text-2xl md:text-3xl tracking-tight mb-3">
                    Thanks, your details have been sent to My Local.
                  </h2>
                  <p className="text-zinc-600 max-w-md mx-auto">
                    We'll be in touch soon. In the meantime, feel free to keep
                    browsing the site or download the app.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setForm(INITIAL);
                      }}
                      data-testid="submit-another-btn"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-zinc-900 font-semibold border border-zinc-200 hover:bg-zinc-50 transition-colors"
                    >
                      Submit another
                    </button>
                    <button
                      onClick={() => navigate("/")}
                      data-testid="success-home-btn"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full brand-gradient text-white font-semibold hover:-translate-y-0.5 transition-transform"
                    >
                      Back to home
                      <ArrowRight size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="partner-signup-form" noValidate>
                  <div className="mb-8 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl brand-gradient text-white">
                      <Storefront size={20} weight="duotone" />
                    </span>
                    <div>
                      <h2 className="font-display font-extrabold text-xl md:text-2xl tracking-tight text-zinc-900">
                        Tell us about your business
                      </h2>
                      <p className="text-sm text-zinc-500">Takes less than a minute.</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <Label required>What are you after?</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="service-picker">
                      {SERVICE_OPTIONS.map((s) => {
                        const active = form.services.includes(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => toggleService(s.id)}
                            data-testid={`service-${s.id.toLowerCase().replace(/\s+/g, "-")}`}
                            aria-pressed={active}
                            className={`relative text-left rounded-2xl border p-4 transition-all ${
                              active
                                ? "border-transparent brand-gradient text-white shadow-md shadow-[#FF1F8B]/25"
                                : "bg-white border-zinc-200 text-zinc-900 hover:border-zinc-300"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <s.icon size={22} weight="duotone" className={active ? "text-white" : "text-[#FF1F8B]"} />
                              {active && <CheckCircle size={18} weight="fill" className="text-white" />}
                            </div>
                            <p className="text-sm font-semibold">{s.title}</p>
                            <p className={`text-xs mt-1 ${active ? "text-white/85" : "text-zinc-500"}`}>{s.blurb}</p>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-zinc-500">Pick one or both.</p>
                    <FieldError msg={errors.services} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <Label htmlFor="business_name" required>Business name</Label>
                      <input
                        id="business_name"
                        data-testid="input-business-name"
                        type="text"
                        value={form.business_name}
                        onChange={update("business_name")}
                        placeholder="Enter your business name"
                        className={inputClass}
                        autoComplete="organization"
                      />
                      <FieldError msg={errors.business_name} />
                    </div>

                    <div>
                      <Label htmlFor="contact_name" required>Contact name</Label>
                      <input
                        id="contact_name"
                        data-testid="input-contact-name"
                        type="text"
                        value={form.contact_name}
                        onChange={update("contact_name")}
                        placeholder="Your name"
                        className={inputClass}
                        autoComplete="name"
                      />
                      <FieldError msg={errors.contact_name} />
                    </div>

                    <div>
                      <Label htmlFor="business_type" required>Business type</Label>
                      <div className="relative">
                        <select
                          id="business_type"
                          data-testid="input-business-type"
                          value={form.business_type}
                          onChange={update("business_type")}
                          className={`${inputClass} appearance-none pr-10`}
                        >
                          <option value="" disabled>Select a type</option>
                          {BUSINESS_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <svg
                          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400"
                          width="14" height="14" viewBox="0 0 20 20" fill="none"
                        >
                          <path d="M5 7l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <FieldError msg={errors.business_type} />
                    </div>

                    <div>
                      <Label htmlFor="email" required>Email address</Label>
                      <input
                        id="email"
                        data-testid="input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="your@email.com"
                        className={inputClass}
                        autoComplete="email"
                      />
                      <FieldError msg={errors.email} />
                    </div>

                    <div>
                      <Label htmlFor="phone" required>Phone number</Label>
                      <input
                        id="phone"
                        data-testid="input-phone"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="Your contact number"
                        className={inputClass}
                        autoComplete="tel"
                      />
                      <FieldError msg={errors.phone} />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="address" required>Business address</Label>
                      <textarea
                        id="address"
                        data-testid="input-address"
                        value={form.address}
                        onChange={update("address")}
                        placeholder="Enter your business address"
                        rows={3}
                        className={`${inputClass} resize-none`}
                        autoComplete="street-address"
                      />
                      <FieldError msg={errors.address} />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="message">Message / notes</Label>
                      <textarea
                        id="message"
                        data-testid="input-message"
                        value={form.message}
                        onChange={update("message")}
                        placeholder="Tell us anything useful about your business"
                        rows={4}
                        className={`${inputClass} resize-none`}
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="submit-partner-btn"
                      className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full brand-gradient text-white font-semibold shadow-lg shadow-[#FF1F8B]/30 disabled:opacity-60 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#FF1F8B]/40 transition-all"
                    >
                      {loading ? "Sending…" : "Submit business interest"}
                      {!loading && (
                        <ArrowRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                      )}
                    </button>
                    <p className="text-xs text-zinc-500 sm:flex-1">
                      By submitting, you agree we can contact you about joining
                      My Local. We won't share your details.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
